import { SubscriptionOptionType } from 'containers/PDP/HeroContainer';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Promo,
  discountedPrice,
  promoAppliesToProduct,
} from 'utils/helpers/activePromoHelpers';

import * as S from './styles';

export type PricingProps = {
  price: number;
  activeSubscriptionOption?: SubscriptionOptionType;
  promo?: Promo;
  productId: number;
  promotionable?: boolean;
  memberPrice?: string;
  subscribeSelected: boolean;
  memberHeroCopy?: string;
  className?: string;
  isTextReduced?: boolean;
  promoText?: string;
  isOutOfStock?: boolean;
  showNewAboveTheFoldPdp?: boolean;
};

const Pricing = ({
  price,
  activeSubscriptionOption,
  promo,
  productId,
  promotionable,
  memberPrice,
  subscribeSelected,
  memberHeroCopy,
  className,
  isTextReduced,
  promoText,
  isOutOfStock = false,
  showNewAboveTheFoldPdp = false,
}: PricingProps): JSX.Element => {
  const formattedPrice = (+price).toFixed(0);
  const formattedSubscriptionPrice = useMemo(
    () =>
      activeSubscriptionOption && (+activeSubscriptionOption.price).toFixed(0),
    [activeSubscriptionOption],
  );

  const promoCode = promo?.code;
  const discountedPromoPrice = discountedPrice(price, promo);
  const isZeroDiscountedPrice = +discountedPromoPrice === 0;
  const isPromoted =
    promo && promoAppliesToProduct(productId, promo, promotionable);

  const showPromo =
    isPromoted && !memberPrice && !subscribeSelected && !isZeroDiscountedPrice;

  const [showDiscount, setShowPromo] = useState<boolean>(false);
  const [hasPromoInfo, sethasPromoInfo] = useState<boolean>(false);

  useEffect(() => {
    setShowPromo(subscribeSelected || !!memberPrice || !!showPromo);
    sethasPromoInfo(true);
  }, [subscribeSelected, memberPrice, showPromo]);

  const renderDiscountPriceDisplay = () => {
    if (subscribeSelected) {
      return `$${formattedSubscriptionPrice}`;
    }
    if (memberPrice) {
      return memberPrice;
    }
    if (showPromo) {
      return `$${discountedPromoPrice}`;
    }
    return null;
  };

  const renderDiscountText = () => {
    if (subscribeSelected) {
      return activeSubscriptionOption?.savingsStatement;
    }
    if (memberHeroCopy) {
      return memberHeroCopy;
    }
    if (showPromo) {
      return `with code ${promoCode?.toUpperCase()}`;
    }
    return null;
  };

  const PriceSection = ({ children }: { children: React.ReactNode }) => (
    <S.PriceSection
      data-testid="pricing-wrapper"
      showDiscount={showDiscount && !isOutOfStock}
      className={className}
      isTextReduced={isTextReduced}
    >
      {children}
    </S.PriceSection>
  );

  return (
    <PriceSection>
      {hasPromoInfo && !isOutOfStock ? (
        <>
          <S.PriceAmount data-testid="price-with-promo">
            <S.ProductPrice
              showNewAboveTheFoldPdp={showNewAboveTheFoldPdp}
            >{`$${formattedPrice}`}</S.ProductPrice>
            <S.DiscountedPrice showNewAboveTheFoldPdp={showNewAboveTheFoldPdp}>
              {renderDiscountPriceDisplay()}
            </S.DiscountedPrice>
          </S.PriceAmount>
          <S.SavingsText>{renderDiscountText()}</S.SavingsText>{' '}
          {promoText ? <S.PromoText>{promoText}</S.PromoText> : null}
        </>
      ) : (
        /* Show the regular price iniitally, once the use effect hook runs we will have
        promo info updated and we can show the block above with an eventual stikethru */
        <S.PriceAmount data-testid="price-without-promo">
          <S.ProductPrice
            showNewAboveTheFoldPdp={showNewAboveTheFoldPdp}
          >{`$${formattedPrice}`}</S.ProductPrice>
        </S.PriceAmount>
      )}
    </PriceSection>
  );
};

export default Pricing;
